import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Home,
  Settings,
  User,
  Plus,
  LogOut,
  Keyboard,
} from 'lucide-react';
import { useAuthStore } from '@/stores';

export interface Command {
  id: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  shortcut?: string;
  action: () => void;
  category: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const commands = useMemo<Command[]>(() => {
    const cmds: Command[] = [
      {
        id: 'home',
        title: '首页',
        description: '返回首页',
        icon: <Home className="h-4 w-4" />,
        shortcut: 'G H',
        action: () => {
          navigate('/');
          onClose();
        },
        category: '导航',
      },
      {
        id: 'pages',
        title: '页面列表',
        description: '查看所有页面',
        icon: <FileText className="h-4 w-4" />,
        shortcut: 'G P',
        action: () => {
          navigate('/pages');
          onClose();
        },
        category: '导航',
      },
      {
        id: 'search',
        title: '搜索',
        description: '搜索页面内容',
        icon: <Search className="h-4 w-4" />,
        shortcut: '/',
        action: () => {
          navigate('/search');
          onClose();
        },
        category: '导航',
      },
      {
        id: 'new-page',
        title: '新建页面',
        description: '创建新页面',
        icon: <Plus className="h-4 w-4" />,
        shortcut: 'N',
        action: () => {
          navigate('/pages/new');
          onClose();
        },
        category: '操作',
      },
      {
        id: 'profile',
        title: '个人资料',
        description: '查看和编辑个人资料',
        icon: <User className="h-4 w-4" />,
        shortcut: 'G U',
        action: () => {
          navigate('/profile');
          onClose();
        },
        category: '导航',
      },
      {
        id: 'api-keys',
        title: 'API Keys',
        description: '管理 API 密钥',
        icon: <Settings className="h-4 w-4" />,
        action: () => {
          navigate('/profile/api-keys');
          onClose();
        },
        category: '导航',
      },
      {
        id: 'favorites',
        title: '收藏页面',
        description: '查看收藏的页面',
        icon: <FileText className="h-4 w-4" />,
        action: () => {
          navigate('/profile/favorites');
          onClose();
        },
        category: '导航',
      },
      {
        id: 'shortcuts',
        title: '键盘快捷键',
        description: '查看所有快捷键',
        icon: <Keyboard className="h-4 w-4" />,
        shortcut: '?',
        action: () => {
          // Show shortcuts modal - we'll handle this separately
          alert('快捷键:\n\n导航:\n  G H - 首页\n  G P - 页面列表\n  G U - 个人资料\n  / - 搜索\n\n操作:\n  N - 新建页面\n  Cmd/Ctrl + K - 命令面板\n  Esc - 关闭面板\n\n编辑器:\n  Cmd/Ctrl + B - 加粗\n  Cmd/Ctrl + I - 斜体\n  Cmd/Ctrl + S - 保存');
          onClose();
        },
        category: '帮助',
      },
    ];

    // Add admin commands if user is admin
    if (user?.role === 'admin') {
      cmds.push({
        id: 'admin',
        title: '管理面板',
        description: '打开管理面板',
        icon: <Settings className="h-4 w-4" />,
        shortcut: 'G A',
        action: () => {
          navigate('/admin');
          onClose();
        },
        category: '导航',
      });
    }

    // Add logout command
    cmds.push({
      id: 'logout',
      title: '退出登录',
      description: '退出当前账号',
      icon: <LogOut className="h-4 w-4" />,
      action: () => {
        logout();
        navigate('/login');
        onClose();
      },
      category: '账户',
    });

    return cmds;
  }, [navigate, onClose, user?.role, logout]);

  const filteredCommands = useMemo(() => {
    if (!search) return commands;
    const lowerSearch = search.toLowerCase();
    return commands.filter(
      (cmd) =>
        cmd.title.toLowerCase().includes(lowerSearch) ||
        cmd.description?.toLowerCase().includes(lowerSearch) ||
        cmd.category.toLowerCase().includes(lowerSearch)
    );
  }, [commands, search]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, onClose]);

  // Reset search when closing
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, Command[]> = {};
    filteredCommands.forEach((cmd) => {
      if (!groups[cmd.category]) {
        groups[cmd.category] = [];
      }
      groups[cmd.category].push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg">
        <div className="bg-popover border border-border rounded-lg shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索命令..."
                className="flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                autoFocus
              />
              <kbd className="px-2 py-0.5 text-xs bg-muted rounded border border-border">
                Esc
              </kbd>
            </div>
          </div>

          {/* Commands list */}
          <div className="max-h-80 overflow-y-auto p-2">
            {Object.entries(groupedCommands).map(([category, cmds]) => (
              <div key={category} className="mb-2">
                <div className="text-xs text-muted-foreground px-2 py-1.5 font-medium">
                  {category}
                </div>
                {cmds.map((cmd) => {
                  const globalIndex = filteredCommands.indexOf(cmd);
                  return (
                    <button
                      key={cmd.id}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                        globalIndex === selectedIndex
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                      onClick={cmd.action}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                    >
                      <span className={globalIndex === selectedIndex ? 'text-primary-foreground' : 'text-muted-foreground'}>
                        {cmd.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{cmd.title}</div>
                        {cmd.description && (
                          <div className={`text-xs truncate ${
                            globalIndex === selectedIndex ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}>
                            {cmd.description}
                          </div>
                        )}
                      </div>
                      {cmd.shortcut && (
                        <kbd className={`px-2 py-0.5 text-xs rounded border ${
                          globalIndex === selectedIndex
                            ? 'bg-primary-foreground/20 border-primary-foreground/30 text-primary-foreground'
                            : 'bg-muted border-border text-muted-foreground'
                        }`}>
                          {cmd.shortcut}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}

            {filteredCommands.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                没有找到匹配的命令
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">↓</kbd>
                选择
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">Enter</kbd>
                确认
              </span>
            </div>
            <span>
              <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">?</kbd>
              查看快捷键
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Global keyboard shortcuts hook
export function useGlobalShortcuts() {
  const navigate = useNavigate();
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);

  // Handle global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        // Still allow Cmd/Ctrl + K to open palette
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          setIsPaletteOpen(true);
        }
        return;
      }

      // Command palette: Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsPaletteOpen(true);
        return;
      }

      // Navigation shortcuts with G prefix
      if (e.key === 'g') {
        // Set up for next key
        const handleNextKey = (nextEvent: KeyboardEvent) => {
          window.removeEventListener('keydown', handleNextKey);
          switch (nextEvent.key) {
            case 'h':
              navigate('/');
              break;
            case 'p':
              navigate('/pages');
              break;
            case 'u':
              navigate('/profile');
              break;
            case 'a':
              navigate('/admin');
              break;
          }
        };
        window.addEventListener('keydown', handleNextKey, { once: true });
        setTimeout(() => window.removeEventListener('keydown', handleNextKey), 1000);
        return;
      }

      // Quick shortcuts
      switch (e.key) {
        case '/':
          e.preventDefault();
          navigate('/search');
          break;
        case 'n':
          e.preventDefault();
          navigate('/pages/new');
          break;
        case '?':
          e.preventDefault();
          setIsPaletteOpen(true);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return { isPaletteOpen, setIsPaletteOpen };
}
